/**
 * The rider's food-order rules after the order is placed (D571 FOOD-CANCEL-RATING, doc 22; owner decisions of 2026-10-13, iFood/99 model).
 *
 * `food.consumerPolicy` is ONE governed settings-kernel key (D393, ADR-0060: four eyes on every change, versioned, effective-dated,
 * content-hashed) holding the five numbers the rider's order obeys once the store has accepted it:
 *
 *   - `cancelRequestWindowSeconds` - after the store ACCEPTS, the rider can only REQUEST the cancellation; the store has this long to
 *     approve or refuse. Silence until the window ends = the order continues (default 300 s, the owner's 5 minutes);
 *   - `lateToleranceSeconds` - if the preparation passes the store's estimated time plus this tolerance, the rider may cancel with a FULL
 *     refund even when the store refuses (default 600 s, the owner's 10 minutes);
 *   - `chronicDelaySeconds` - past the estimate plus this, the PLATFORM cancels the order itself and refunds in full (default 1800 s);
 *   - `noCourierSeconds` - a ready order that has had no courier for this long is cancelled by the platform, refunded in full (default 900 s);
 *   - `ratingWindowDays` - how long after the delivery the rider can still rate the merchant and the courier (default 7 days).
 *
 * D576 FOOD-FAULT-RULES (doc 22; owner rule of 2026-10-13: the iFood / 99Food / Keeta baseline, slightly improved) adds four members:
 *
 *   - `prepExtensionMaxSeconds` - the store may EXTEND the preparation estimate frozen at its accept ONCE, by at most this (default 900 s); an
 *     extension notifies the rider and opens a free-cancel right for the rider (decision reason `PREP_EXTENDED_RIDER_CANCEL`);
 *   - `partialRefundWindowSeconds` - the store may answer a cancellation request with a PARTIAL-refund proposal; the rider has this long to
 *     accept (the order is cancelled with that refund) or refuse (the order continues); silence = refuse (default 900 s, the iFood 15 minutes);
 *   - `courierDisplacementBps` - when a courier already assigned to the order is left without the trip, the share of the courier's trip fee
 *     (his frozen split, tip excluded) paid for the displacement while he had not yet arrived at the store (default 3000 = 30 %); once he
 *     arrived at or collected from the store the FULL trip fee is paid;
 *   - `courierDisplacementMinMinor` - a floor for that displacement fee in the market's minor currency units (default 0: none; a market sets
 *     its own number, so no currency is hardcoded here).
 *
 * D854 (L3-012) adds the tenth member, on the other side of the same order:
 *
 *   - `merchantAcceptWindowSeconds` - how long the STORE has to accept or refuse a submitted order; past it the platform refuses the order for
 *     silence (`auto_rejected_timeout`) and the rider is told (default 480 s, the owner's 8 minutes of D435 5 #18; bounds 120-1800 s). A market
 *     never lengthens its country's window (the shorter binds, so a rider never waits longer than the country allows).
 *
 * The five members after the first five are OPTIONAL when a version is published (a version published before D576 carries five and keeps
 * working: the reader fills the missing ones with the defaults above); the value the rules read always has all ten.
 *
 * Scopes: country and market, OVERRIDABLE. A market never LOOSENS its country: the reader binds, field by field, the value that protects
 * the rider - the SHORTER of the market's and the country's four durations, the LONGER rating window (AGENTS: a lower scope never loosens
 * a country-level constraint). The pre-D571 behaviour of an ungoverned market is the owner's defaults above (the registry default), so
 * publishing nothing changes no market. A version the reader cannot parse or verify fails CLOSED: nothing is cancelled, requested or rated
 * under it (`FOOD_CONSUMER_POLICY_INVALID`).
 *
 * Every decision carries the policy version string (`food.consumerPolicy@<scope>:<ref>#v<n>`, or `@default`; `+<country version>` when the
 * country's tighter value bound a field), its content hash and its change id. Neutral: no country, currency or locale is named here.
 */
import { policySetting, resolvePolicy } from './policy.js';
import { SCOPE_REF_KIND, marketSettingContext, settingScopeRefIn, settingScopeRefValid } from './setting-context.js';
import { settingSnapshotFields } from './setting-kernel.js';
/** The governed key (spelled as data in `KERNEL_SETTINGS`, the mirror of `vello.setting_definitions`). */
export const FOOD_CONSUMER_POLICY_KEY = 'food.consumerPolicy';
/** The owner's accepted defaults (2026-10-13): 5 minutes to answer, 10 minutes of tolerance, 7 days to rate. */
export const DEFAULT_FOOD_CONSUMER_POLICY = {
    cancelRequestWindowSeconds: 300,
    lateToleranceSeconds: 600,
    chronicDelaySeconds: 1_800,
    noCourierSeconds: 900,
    ratingWindowDays: 7,
    prepExtensionMaxSeconds: 900,
    partialRefundWindowSeconds: 900,
    courierDisplacementBps: 3_000,
    courierDisplacementMinMinor: 0,
    merchantAcceptWindowSeconds: 480,
};
/** Closed bounds of every member (the same numbers the migration's jsonpath floor holds). */
export const FOOD_CONSUMER_POLICY_BOUNDS = {
    cancelRequestWindowSeconds: { min: 60, max: 3_600 },
    lateToleranceSeconds: { min: 60, max: 7_200 },
    chronicDelaySeconds: { min: 300, max: 14_400 },
    noCourierSeconds: { min: 120, max: 7_200 },
    ratingWindowDays: { min: 1, max: 30 },
    prepExtensionMaxSeconds: { min: 60, max: 3_600 },
    partialRefundWindowSeconds: { min: 60, max: 3_600 },
    courierDisplacementBps: { min: 0, max: 10_000 },
    courierDisplacementMinMinor: { min: 0, max: 1_000_000_000 },
    merchantAcceptWindowSeconds: { min: 120, max: 1_800 },
};
const MEMBERS = Object.keys(FOOD_CONSUMER_POLICY_BOUNDS);
/** The members a version published before D576 does not carry (optional on input, defaulted on read). */
export const FOOD_CONSUMER_POLICY_OPTIONAL_MEMBERS = ['prepExtensionMaxSeconds', 'partialRefundWindowSeconds', 'courierDisplacementBps', 'courierDisplacementMinMinor', 'merchantAcceptWindowSeconds'];
const REQUIRED_MEMBERS = MEMBERS.filter((m) => !FOOD_CONSUMER_POLICY_OPTIONAL_MEMBERS.includes(m));
export const FOOD_CONSUMER_POLICY_REASON_CODES = ['FOOD_CONSUMER_POLICY_GOVERNED', 'FOOD_CONSUMER_POLICY_DEFAULT', 'FOOD_CONSUMER_POLICY_INVALID'];
const MARKET_ID = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** The stable issue of a value that is not a food consumer policy, or undefined when it is one. */
export function foodConsumerPolicyIssue(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return 'NOT_AN_OBJECT';
    const o = v;
    const keys = Object.keys(o);
    if (keys.some((k) => !MEMBERS.includes(k)) || REQUIRED_MEMBERS.some((m) => !keys.includes(m)))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    for (const m of MEMBERS) {
        if (!keys.includes(m))
            continue;
        const n = o[m];
        const b = FOOD_CONSUMER_POLICY_BOUNDS[m];
        if (typeof n !== 'number' || !Number.isInteger(n) || n < b.min || n > b.max)
            return `INVALID_${m.replace(/[A-Z]/g, (c) => `_${c}`).toUpperCase()}`;
    }
    // The platform must not cancel on its own before the rider's own right to cancel opens.
    if (o.chronicDelaySeconds < o.lateToleranceSeconds)
        return 'CHRONIC_DELAY_BEFORE_LATE_TOLERANCE';
    return undefined;
}
export function validFoodConsumerPolicyValue(v) {
    return foodConsumerPolicyIssue(v) === undefined;
}
/** A published version with the D576 members filled by their defaults (a pre-D576 version reads exactly as it did). */
export function completeFoodConsumerPolicy(v) {
    return { ...DEFAULT_FOOD_CONSUMER_POLICY, ...v };
}
/** `food.consumerPolicy@<scope>:<ref>#v<n>`. */
export function foodConsumerPolicyVersionOf(scope, scopeRef, version) {
    return `${FOOD_CONSUMER_POLICY_KEY}@${scope}:${scopeRef}#v${version}`;
}
/** Build a valid version (country, state or market scope). Throws a RangeError on a malformed scope ref or value. */
export function foodConsumerPolicySetting(input) {
    if (!settingScopeRefValid(input.scope, input.scopeRef)) {
        throw new RangeError(`a ${input.scope}-scope food consumer policy needs a valid ${SCOPE_REF_KIND[input.scope]}, got ${input.scopeRef}`);
    }
    const issue = foodConsumerPolicyIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid food consumer policy: ${issue}`);
    return policySetting({
        key: FOOD_CONSUMER_POLICY_KEY,
        scope: input.scope,
        scopeRef: input.scopeRef,
        value: { ...input.value },
        version: input.version,
        effectiveFrom: input.effectiveFrom,
        ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}),
        mode: 'OVERRIDABLE',
    });
}
/** The rider-protective combination of a market's value with its country's (see the header). */
function bound(market, country) {
    const value = {
        cancelRequestWindowSeconds: Math.min(market.cancelRequestWindowSeconds, country.cancelRequestWindowSeconds),
        lateToleranceSeconds: Math.min(market.lateToleranceSeconds, country.lateToleranceSeconds),
        chronicDelaySeconds: Math.min(market.chronicDelaySeconds, country.chronicDelaySeconds),
        noCourierSeconds: Math.min(market.noCourierSeconds, country.noCourierSeconds),
        ratingWindowDays: Math.max(market.ratingWindowDays, country.ratingWindowDays),
        // D576: a shorter extension is the rider-protective one; a longer proposal window, a higher displacement share and floor protect the rider / courier.
        prepExtensionMaxSeconds: Math.min(market.prepExtensionMaxSeconds, country.prepExtensionMaxSeconds),
        partialRefundWindowSeconds: Math.max(market.partialRefundWindowSeconds, country.partialRefundWindowSeconds),
        courierDisplacementBps: Math.max(market.courierDisplacementBps, country.courierDisplacementBps),
        courierDisplacementMinMinor: Math.max(market.courierDisplacementMinMinor, country.courierDisplacementMinMinor),
        // D854: a shorter accept window is the one that keeps the rider from waiting on a silent store.
        merchantAcceptWindowSeconds: Math.min(market.merchantAcceptWindowSeconds, country.merchantAcceptWindowSeconds),
    };
    return { value, overruled: MEMBERS.some((m) => value[m] !== market[m]) };
}
/**
 * The rules in force for `marketId` at `at`, over the GOVERNED reader's versions. No scope governs -> the owner's defaults. A state or market
 * version never loosens its country's (field by field, the rider-protective value binds and the decision names both versions); the most specific
 * of the state and the market decides (`regionCode` is the market's registered state, ADR-0124 - a market with none skips the layer).
 */
export function resolveFoodConsumerPolicy(settings, marketId, at, regionCode) {
    const invalid = { ok: false, reasonCode: 'FOOD_CONSUMER_POLICY_INVALID', policyVersion: `${FOOD_CONSUMER_POLICY_KEY}@invalid` };
    if (!MARKET_ID.test(marketId))
        return invalid;
    const ctx = marketSettingContext(marketId, regionCode);
    const country = ctx.country;
    const relevant = settings.filter((s) => s.key === FOOD_CONSUMER_POLICY_KEY && (s.scope === 'country' || s.scope === 'region' || s.scope === 'market'));
    const resolution = resolvePolicy(relevant, ctx, FOOD_CONSUMER_POLICY_KEY, at);
    if (resolution.reason === 'NOT_SET') {
        return { ok: true, value: DEFAULT_FOOD_CONSUMER_POLICY, reasonCode: 'FOOD_CONSUMER_POLICY_DEFAULT', policyVersion: `${FOOD_CONSUMER_POLICY_KEY}@default` };
    }
    if (resolution.scope === undefined || resolution.version === undefined || (resolution.scope !== 'country' && resolution.scope !== 'region' && resolution.scope !== 'market'))
        return invalid;
    if (!validFoodConsumerPolicyValue(resolution.value))
        return invalid;
    const ref = settingScopeRefIn(resolution.scope, ctx) ?? country;
    let value = completeFoodConsumerPolicy(resolution.value);
    let policyVersion = foodConsumerPolicyVersionOf(resolution.scope, ref, resolution.version);
    if (resolution.scope !== 'country') {
        const countryOnly = resolvePolicy(relevant.filter((s) => s.scope === 'country'), { country }, FOOD_CONSUMER_POLICY_KEY, at);
        if (countryOnly.reason !== 'NOT_SET') {
            if (!validFoodConsumerPolicyValue(countryOnly.value) || countryOnly.version === undefined)
                return invalid;
            const bounded = bound(value, completeFoodConsumerPolicy(countryOnly.value));
            value = bounded.value;
            if (bounded.overruled)
                policyVersion = `${policyVersion}+${foodConsumerPolicyVersionOf('country', country, countryOnly.version)}`;
        }
    }
    const snap = settingSnapshotFields(relevant, FOOD_CONSUMER_POLICY_KEY, resolution.scope, ref, resolution.version);
    return {
        ok: true,
        value: { ...value },
        reasonCode: 'FOOD_CONSUMER_POLICY_GOVERNED',
        policyVersion,
        ...(snap.contentHash !== undefined ? { contentHash: snap.contentHash } : {}),
        ...(snap.changeId !== undefined ? { changeId: snap.changeId } : {}),
    };
}
//# sourceMappingURL=food-consumer-policy.js.map